import {
  Box,
  Button,
  CloseButton,
  Container,
  Flex,
  Group,
  keyframes,
  Loader,
  MultiSelect,
  rem,
  Stack,
  Text
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconBriefcase, IconCheck, IconCode } from '@tabler/icons-react'
import { useSession } from 'next-auth/react'

import Head from 'next/head'
import Link from 'next/link'
import {
  forwardRef,
  startTransition,
  Suspense,
  useEffect,
  useMemo,
  useState
} from 'react'
import EmbeddedCheckoutButton from '../components/buttons/checkout_button.js'
import Layout from '../components/layout'
import { searchAPI_v2 } from '../utils/axios.js'
import prerenderImportantLocales from '../utils/prerender_locales.js'
import SsdCountries from '../utils/ss_countries.json'
import SsdPlatforms from '../utils/ss_platforms.json'

// Constantes y animaciones
const PURPLE_PRIMARY = 'rgba(123,57,182,1)'
const PURPLE_LIGHT = 'rgba(123,57,182,0.8)'
const PURPLE_LIGHTER = 'rgba(123,57,182,0.5)'
const PURPLE_FAINT = 'rgba(123,57,182,0.1)'
const PURPLE_ULTRA_FAINT = 'rgba(123,57,182,0.02)'

function Value({ label, price, onRemove, ...others }) {
  return (
    <div {...others}>
      <Box
        sx={(theme) => ({
          display: 'flex',
          cursor: 'default',
          alignItems: 'center',
          backgroundColor:
            theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
          border: `${rem(1)} solid ${
            theme.colorScheme === 'dark'
              ? theme.colors.dark[7]
              : theme.colors.gray[4]
          }`,
          paddingLeft: theme.spacing.xs,
          borderRadius: theme.radius.sm
        })}
      >
        <Box sx={{ lineHeight: 1, fontSize: rem(12) }} mr={10}>
          {label}
        </Box>
        <Box sx={{ lineHeight: 1, fontSize: rem(12) }}>
          <Text fw={700} color="green">
            ${price.toFixed(2)}
          </Text>
        </Box>
        <CloseButton
          onMouseDown={onRemove}
          variant="transparent"
          size={22}
          iconSize={14}
          tabIndex={-1}
        />
      </Box>
    </div>
  )
}

const Item = forwardRef(({ label, price, ...others }, ref) => {
  return (
    <div ref={ref} {...others}>
      <Flex align="center">
        <Box sx={{ lineHeight: 1, fontSize: rem(12) }} mr={10}>
          {label}
        </Box>
        <Box sx={{ lineHeight: 1, fontSize: rem(12) }}>
          <Text fw={700} color="green">
            ${price.toFixed(2)}
          </Text>
        </Box>
      </Flex>
    </div>
  )
})

Item.displayName = 'Item'

const glowAnimation = keyframes({
  '0%': { boxShadow: `0 0 5px ${PURPLE_LIGHTER}` },
  '50%': { boxShadow: `0 0 20px ${PURPLE_LIGHTER}` },
  '100%': { boxShadow: `0 0 5px ${PURPLE_LIGHTER}` }
})

function PricingContent({ topPlatforms }) {
  const { data: session, status } = useSession()
  const [mounted, setMounted] = useState(false)
  const [ssdValue, setSsdValue] = useState([])
  const [ssdCountryValue, setSsdCountryValue] = useState([])
  const [isBusinessTools, setIsBusinessTools] = useState(true)

  const plans = [
    /*{
      name: 'Básico',
      price: '29.99',
      description: 'Para usuarios individuales y pequeños equipos',
      features: [
        'Where to Watch personalizable',
        'Tracking Content (500 búsquedas/mes)',
        'API calls (1000/mes)',
        'Almacenamiento S3 (5GB)',
        'Soporte por email',
        'Análisis básicos',
        'Actualizaciones semanales',
        'Integraciones básicas'
      ],
      highlight: false,
      price_id: 'price_1R2GheHPpCgfP5oNW0E6hWyC'
    },
    {
      name: 'Pro',
      price: '99.99',
      description: 'Para equipos profesionales que necesitan más potencia',
      features: [
        'Where to Watch premium',
        'Tracking Content (5000 búsquedas/mes)',
        'API calls (10000/mes)',
        'Almacenamiento S3 (50GB)',
        'Soporte prioritario',
        'Análisis avanzados',
        'Actualizaciones diarias',
        'Integraciones avanzadas',
        'Panel de administración',
        'Reportes personalizados'
      ],
      highlight: true,
      price_id: 'price_1R2Xh0HPpCgfP5oN3IdIn9kt'
    },
    {
      name: 'Enterprise',
      price: 'Personalizado',
      description: 'Solución personalizada para grandes organizaciones',
      features: [
        'Where to Watch enterprise',
        'Tracking Content ilimitado',
        'API calls ilimitadas',
        'Almacenamiento S3 ilimitado',
        'Soporte 24/7 dedicado',
        'Análisis enterprise',
        'Actualizaciones en tiempo real',
        'Integraciones personalizadas',
        'Panel multi-equipo',
        'Reportes automatizados',
        'Account Manager dedicado',
        'SLA garantizado'
      ],
      highlight: false
    }*/
  ]

  const options = useMemo(
    () => [
      ...topPlatforms.map((platform) => ({
        label: platform.platform,
        value: SsdPlatforms.find((p) => p.global_id === platform.id).priceId,
        group: 'Popular',
        price: SsdPlatforms.find((p) => p.global_id === platform.id).price
      })),
      ...SsdPlatforms.map((platform) => ({
        label: platform.name,
        value: platform.priceId,
        group: 'All',
        price: platform.price
      }))
        .filter((p) => !topPlatforms.some((tp) => tp.id === p.value))
        .reduce((acc, curr) => {
          if (!acc.some((p) => p.value === curr.value)) {
            acc.push(curr)
          }
          return acc
        }, [])
    ],
    [topPlatforms]
  )

  const countryOptions = useMemo(
    () =>
      SsdCountries.map((country) => ({
        label: country.country,
        value: country.priceId,
        price: country.price
      })),
    []
  )

  // Efecto para manejar el montaje y cargar datos del localStorage
  useEffect(() => {
    startTransition(() => {
      const savedPlatforms = localStorage.getItem('selectedPlatforms')
      const savedCountries = localStorage.getItem('selectedCountries')

      // Obtener el parámetro de la URL
      const urlParams = new URLSearchParams(window.location.search)
      const platformParam = urlParams.get('platform')

      if (platformParam) {
        // Verificar si el parámetro es un global_id o un priceId
        const isGlobalId = SsdPlatforms.some(
          (p) => p.global_id.toString() === platformParam
        )

        if (isGlobalId) {
          // Si es un global_id, encontrar el priceId correspondiente
          const platform = SsdPlatforms.find(
            (p) => p.global_id.toString() === platformParam
          )
          if (platform) {
            setSsdValue([platform.priceId])
          }
        } else {
          // Si es un priceId, usarlo directamente
          setSsdValue([platformParam])
        }
      } else if (savedPlatforms) {
        // Si no hay parámetro, usar el valor guardado en localStorage
        setSsdValue(JSON.parse(savedPlatforms))
      }

      if (savedCountries) {
        setSsdCountryValue(JSON.parse(savedCountries))
      }

      setMounted(true)
    })
  }, [])

  // Efecto para guardar las selecciones cuando cambien
  useEffect(() => {
    if (mounted) {
      startTransition(() => {
        localStorage.setItem('selectedPlatforms', JSON.stringify(ssdValue))
      })
    }
  }, [ssdValue, mounted])

  useEffect(() => {
    if (mounted) {
      startTransition(() => {
        localStorage.setItem('selectedCountries', JSON.stringify(ssdCountryValue))
      })
    }
  }, [ssdCountryValue, mounted])

  const handlePlatformChange = (value) => {
    if (value.length > 10) {
      notifications.show({
        title: 'Límite de plataformas alcanzado',
        message:
          'Has alcanzado el límite de 10 plataformas. Si necesitas más, por favor contáctanos.',
        color: 'grape',
        autoClose: 5000
      })
      return
    }
    startTransition(() => {
      setSsdValue(value)
    })
  }

  const handleCountryChange = (value) => {
    if (value.length > 10) {
      notifications.show({
        title: 'Límite de países alcanzado',
        message:
          'Has alcanzado el límite de 10 países. Si necesitas más, por favor contáctanos.',
        color: 'grape',
        autoClose: 5000
      })
      return
    }
    startTransition(() => {
      setSsdCountryValue(value)
    })
  }

  // Estilos reutilizables
  const cardHoverStyle = {
    transform: 'translateY(-10px)',
    borderColor: PURPLE_LIGHTER,
    boxShadow: '0 10px 30px rgba(123,57,182,0.2)'
  }

  const payAsYouGoCardStyle = (theme) => ({
    textAlign: 'center',
    padding: theme.spacing.md,
    borderRadius: theme.radius.md,
    backgroundColor: PURPLE_ULTRA_FAINT,
    border: '1px solid rgba(123,57,182,0.07)',
    transition: 'all 0.3s ease',
    minWidth: '240px',
    maxWidth: '260px',
    '&:hover': {
      backgroundColor: 'rgba(123,57,182,0.04)',
      transform: 'translateY(-3px)',
      boxShadow: '0 5px 15px rgba(123,57,182,0.05)'
    }
  })

  // Componentes reutilizables
  const FeatureItem = ({ text }) => (
    <Group noWrap spacing="xs">
      <IconCheck size={14} color={PURPLE_PRIMARY} />
      <Text size="xs">{text}</Text>
    </Group>
  )

  const RenderPurchaseButton = ({ priceIds, buttonText }) => {
    if (status === 'loading') {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
          <Loader color="grape" size="sm" />
        </Box>
      )
    }

    if (!session) {
      return (
        <Button
          variant="gradient"
          gradient={{
            from: PURPLE_PRIMARY,
            to: PURPLE_LIGHT
          }}
          fullWidth
          onClick={() => {
            window.location.href = `/login?callbackUrl=${encodeURIComponent(
              window.location.href
            )}&fromPricing=true`
          }}
          mb={20}
          size="sm"
          sx={{
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)'
            }
          }}
        >
          {buttonText}
        </Button>
      )
    }

    return (
      <EmbeddedCheckoutButton
        variant="gradient"
        gradient={{
          from: PURPLE_PRIMARY,
          to: PURPLE_LIGHT
        }}
        fullWidth
        mb={20}
        size="sm"
        sx={{
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'scale(1.05)'
          }
        }}
        buttonText={buttonText}
        priceIds={priceIds}
      />
    )
  }

  // Si no está montado, no renderizamos nada ya que el Suspense mostrará el fallback
  if (!mounted) {
    return null
  }

  return (
    <>
      <Head>
        <title>Pricing - StreamDiscover</title>
        <meta name="description" content="Planes y precios de StreamDiscover" />
      </Head>

      <Container
        size="xl"
        py={{ base: 120, sm: 120, md: 120, lg: 100 }}
        px={{ base: 'md', sm: 'xl', md: 'xl', lg: 'xl' }}
        sx={(theme) => ({
          width: '85vw',
          maxWidth: '1366px',
          margin: '0 auto',
          [theme.fn.smallerThan('md')]: {
            paddingLeft: theme.spacing.md,
            paddingRight: theme.spacing.md,
            width: '100%'
          }
        })}
      >
        <Stack align="center" spacing="xl">
          {/* Selector de tipo de plan */}
          {/*<Box
            sx={() => ({
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: 40
            })}
          >
            <Box
              sx={(theme) => ({
                position: 'relative',
                width: '100%',
                maxWidth: '400px',
                height: '50px',
                backgroundColor:
                  theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
                borderRadius: '25px',
                padding: '5px',
                boxShadow: '0 4px 15px rgba(123,57,182,0.1)',
                border: `1px solid ${
                  theme.colorScheme === 'dark'
                    ? theme.colors.dark[4]
                    : 'rgba(123,57,182,0.1)'
                }`,
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '5px',
                  left: isBusinessTools ? '5px' : '50%',
                  width: 'calc(50% - 10px)',
                  height: 'calc(100% - 10px)',
                  backgroundColor: PURPLE_PRIMARY,
                  borderRadius: '20px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  zIndex: 0
                }
              })}
            >
              <Group
                spacing={0}
                style={{ position: 'relative', zIndex: 1 }}
                w="100%"
                h="100%"
              >
                <Box
                  component="button"
                  onClick={() => setIsBusinessTools(true)}
                  sx={() => ({
                    flex: 1,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 15px',
                    cursor: 'pointer',
                    border: 'none',
                    background: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: isBusinessTools ? 'none' : 'translateY(-1px)'
                    }
                  })}
                >
                  <Group spacing="xs" noWrap>
                    <IconBriefcase
                      size={18}
                      style={{
                        color: isBusinessTools ? 'white' : PURPLE_PRIMARY,
                        transition: 'color 0.3s ease'
                      }}
                    />
                    <Text
                      size="sm"
                      weight={600}
                      sx={{
                        color: isBusinessTools ? 'white' : PURPLE_PRIMARY,
                        transition: 'color 0.3s ease',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Business Tools
                    </Text>
                  </Group>
                </Box>
                <Box
                  component="button"
                  onClick={() => setIsBusinessTools(false)}
                  sx={() => ({
                    flex: 1,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 15px',
                    cursor: 'pointer',
                    border: 'none',
                    background: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: !isBusinessTools ? 'none' : 'translateY(-1px)'
                    }
                  })}
                >
                  <Group spacing="xs" noWrap>
                    <IconCode
                      size={18}
                      style={{
                        color: !isBusinessTools ? 'white' : PURPLE_PRIMARY,
                        transition: 'color 0.3s ease'
                      }}
                    />
                    <Text
                      size="sm"
                      weight={600}
                      sx={{
                        color: !isBusinessTools ? 'white' : PURPLE_PRIMARY,
                        transition: 'color 0.3s ease',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Tracking Content
                    </Text>
                  </Group>
                </Box>
              </Group>
            </Box>
          </Box>*/}
          {/* Planes de precios */}
          <Group
            position="center"
            spacing={{ base: 30, sm: 40, md: 50, lg: 50 }}
            align="stretch"
            w="100%"
            sx={(theme) => ({
              [theme.fn.smallerThan('sm')]: {
                flexDirection: 'column',
                alignItems: 'center'
              }
            })}
          >
            {isBusinessTools
              ? plans.map((plan) => (
                  <Box
                    key={plan.name}
                    sx={(theme) => ({
                      backgroundColor:
                        theme.colorScheme === 'dark' ? '#1A1B1E' : '#fff',
                      borderRadius: theme.radius.md,
                      padding: theme.spacing.xl,
                      flex: '1',
                      minWidth: '220px',
                      maxWidth: { base: '100%', sm: '220px' },
                      width: { base: '100%', sm: 'auto' },
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid',
                      borderColor:
                        theme.colorScheme === 'dark'
                          ? '#2C2E33'
                          : theme.colors.gray[2],
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      margin: '0 10px',
                      top: plan.highlight ? { base: 0, sm: '-20px' } : '0',
                      animation: plan.highlight
                        ? `${glowAnimation} 3s infinite`
                        : 'none',
                      '&:hover': cardHoverStyle,
                      [theme.fn.smallerThan('sm')]: {
                        marginBottom: plan.highlight ? '40px' : '30px',
                        maxWidth: '100%',
                        margin: '0 0 20px 0'
                      }
                    })}
                  >
                    {plan.highlight && (
                      <Text
                        size="xs"
                        weight={600}
                        sx={(theme) => ({
                          position: 'absolute',
                          top: -10,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          backgroundColor: PURPLE_PRIMARY,
                          color: 'white',
                          padding: '3px 10px',
                          borderRadius: '10px',
                          whiteSpace: 'nowrap',
                          [theme.fn.smallerThan('sm')]: {
                            top: -12
                          }
                        })}
                      >
                        Más Popular
                      </Text>
                    )}
                    <Stack spacing="xs" align="flex-start" mb={20}>
                      <Text weight={700} size="lg">
                        {plan.name}
                      </Text>
                      <Text size="xs" c="dimmed" style={{ minHeight: '32px' }}>
                        {plan.description}
                      </Text>

                      <Text size="xl" weight={800} mt={5}>
                        ${plan.price}
                        <Text span size="xs" c="dimmed" weight={400}>
                          /mes
                        </Text>
                      </Text>
                    </Stack>

                    <RenderPurchaseButton
                      priceIds={[plan.price_id]}
                      buttonText="Prueba Gratuita 7 Días"
                    />

                    <Stack spacing="xs" style={{ flex: 1 }}>
                      {plan.features.map((feature) => (
                        <FeatureItem key={feature} text={feature} />
                      ))}
                    </Stack>
                  </Box>
                ))
              : null}
          </Group>
          {/* Sección Pay-as-you-go */}
          <Box
            sx={(theme) => ({
              backgroundColor: PURPLE_ULTRA_FAINT,
              borderRadius: theme.radius.md,
              padding: theme.spacing.xl,
              width: '100%',
              maxWidth: '1095px',
              margin: '0 auto',
              marginTop: '40px',
              border: `1px solid ${PURPLE_FAINT}`,
              transition: 'all 0.3s ease',
              position: 'relative',
              '&:hover': {
                backgroundColor: 'rgba(123,57,182,0.03)',
                transform: 'translateY(-3px)',
                boxShadow: '0 8px 20px rgba(123,57,182,0.07)'
              },
              [theme.fn.smallerThan('md')]: {
                padding: theme.spacing.md
              },
              [theme.fn.smallerThan('sm')]: {
                padding: theme.spacing.sm
              }
            })}
          >
            <Stack spacing="xs">
              <Stack spacing={2} align="center">
                <Text size="lg" weight={600}>
                  Streaming Services Directory
                </Text>
              </Stack>
              <MultiSelect
                value={ssdValue}
                onChange={handlePlatformChange}
                label="Choose your platforms"
                placeholder="Select up to ten"
                data={options}
                searchable
                nothingFound="Nothing found"
                valueComponent={Value}
                itemComponent={Item}
                maxSelectedValues={100}
                limit={100}
              />
              <RenderPurchaseButton
                priceIds={ssdValue}
                buttonText="Prueba Gratuita 7 Días"
              />

              <MultiSelect
                value={ssdCountryValue}
                onChange={handleCountryChange}
                label="Choose your countries"
                placeholder="Select up to ten"
                data={countryOptions}
                searchable
                nothingFound="Nothing found"
                valueComponent={Value}
                itemComponent={Item}
                maxSelectedValues={100}
                limit={100}
              />
              <RenderPurchaseButton
                priceIds={ssdCountryValue}
                buttonText="Prueba Gratuita 7 Días"
              />

              <Group
                position="center"
                spacing={{ base: 20, sm: 25, md: 30 }}
                sx={(theme) => ({
                  [theme.fn.smallerThan('sm')]: {
                    flexDirection: 'column',
                    alignItems: 'center',
                    '& > *': {
                      marginBottom: theme.spacing.md
                    }
                  }
                })}
              >
                <Box
                  sx={(theme) => ({
                    ...payAsYouGoCardStyle(theme),
                    margin: '0 15px'
                  })}
                >
                  <IconCode
                    size={16}
                    color="rgba(123,57,182,0.7)"
                    style={{ marginBottom: 8 }}
                  />
                  <Text weight={600} size="xs" mb={5}>
                    API Calls
                  </Text>
                  <Text size="lg" weight={700} color="grape" mb={2}>
                    $0.001
                  </Text>
                  <Text size="xs" color="dimmed">
                    por llamada
                  </Text>
                </Box>
                <Box
                  sx={(theme) => ({
                    ...payAsYouGoCardStyle(theme),
                    margin: '0 15px'
                  })}
                >
                  <IconBriefcase
                    size={16}
                    color="rgba(123,57,182,0.7)"
                    style={{ marginBottom: 8 }}
                  />
                  <Text weight={600} size="xs" mb={5}>
                    Almacenamiento S3
                  </Text>
                  <Text size="lg" weight={700} color="grape" mb={2}>
                    $0.05
                  </Text>
                  <Text size="xs" color="dimmed">
                    por GB/mes
                  </Text>
                </Box>
                <Box
                  sx={(theme) => ({
                    ...payAsYouGoCardStyle(theme),
                    margin: '0 15px'
                  })}
                >
                  <IconCheck
                    size={16}
                    color="rgba(123,57,182,0.7)"
                    style={{ marginBottom: 8 }}
                  />
                  <Text weight={600} size="xs" mb={5}>
                    Widget Impressions
                  </Text>
                  <Text size="lg" weight={700} color="grape" mb={2}>
                    $0.002
                  </Text>
                  <Text size="xs" color="dimmed">
                    por impresión
                  </Text>
                </Box>
              </Group>

              <Group position="center" mt={10}>
                <Button
                  variant="subtle"
                  color="grape"
                  component={Link}
                  href="/contact-us"
                  size="sm"
                  sx={{
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: PURPLE_FAINT,
                      transform: 'translateY(-2px)'
                    }
                  }}
                  leftIcon={<IconBriefcase size={16} />}
                >
                  Calcular costos estimados
                </Button>
              </Group>
            </Stack>
          </Box>
          {/* Sección de FAQ */}
          <Box mt={{ base: 40, sm: 50, md: 60 }}>
            <Stack align="center" spacing="sm">
              <Text size="xl" weight={700}>
                Preguntas frecuentes
              </Text>
              <Text
                size="sm"
                color="dimmed"
                align="center"
                maw={600}
                mx="auto"
                mb={30}
              >
                Encuentra respuestas a las preguntas más comunes de nuestros usuarios
              </Text>

              <Stack spacing="xl" w="100%" maw={1095}>
                <Box>
                  <Text weight={600} size="md" mb={8}>
                    ¿Qué es la prueba gratuita?
                  </Text>
                  <Text size="sm" color="dimmed">
                    Al registrarte, puedes seleccionar entre los planes Premium,
                    Artist o Manager. Todos los planes ofrecen una prueba gratuita de
                    7 días para que pruebes y explores nuestra herramienta. Después
                    del período de prueba, tu tarjeta será facturada según el plan
                    seleccionado. Si cancelas durante el período de prueba, no se te
                    cobrará y podrás permanecer en el plan Básico.
                  </Text>
                </Box>

                <Box>
                  <Text weight={600} size="md" mb={8}>
                    ¿Cuál es la diferencia entre los planes mensuales y anuales?
                  </Text>
                  <Text size="sm" color="dimmed">
                    Ambos planes ofrecen las mismas características y acceso. Los
                    planes mensuales se facturan mensualmente, mientras que los
                    planes anuales ofrecen una tarifa con descuento y se facturan al
                    inicio de tu suscripción.
                  </Text>
                </Box>

                <Box>
                  <Text weight={600} size="md" mb={8}>
                    ¿Ofrecen acceso API?
                  </Text>
                  <Text size="sm" color="dimmed">
                    Sí, para desarrolladores ofrecemos acceso API, así como
                    compartición de datos y dashboards personalizados. Contáctanos
                    directamente en{' '}
                    <Text component="span" color="grape" sx={{ cursor: 'pointer' }}>
                      hi@streamdiscover.com
                    </Text>{' '}
                    para más información.
                  </Text>
                </Box>

                <Box>
                  <Text weight={600} size="md" mb={8}>
                    ¿Ofrecen soluciones personalizadas?
                  </Text>
                  <Text size="sm" color="dimmed">
                    Podemos adaptarnos a cualquier solicitud personalizada. Ofrecemos
                    dashboards a medida, diseño de informes personalizados y
                    proporcionamos casos de estudio bajo petición. Contáctanos
                    directamente para más información en{' '}
                    <Text component="span" color="grape" sx={{ cursor: 'pointer' }}>
                      hi@streamdiscover.com
                    </Text>
                  </Text>
                </Box>

                <Box>
                  <Text weight={600} size="md" mb={8}>
                    ¿Ofrecen cuentas corporativas?
                  </Text>
                  <Text size="sm" color="dimmed">
                    ¡Sí! Ofrecemos cuentas para equipos corporativos que optimizan la
                    incorporación, gestión y acceso para múltiples usuarios. Además,
                    podrás crear y enviar facturas, establecer términos (previa
                    aprobación), configurar pagos mediante transferencias ACH y
                    transferencias bancarias, etc. Contacta con{' '}
                    <Text component="span" color="grape" sx={{ cursor: 'pointer' }}>
                      hi@streamdiscover.com
                    </Text>{' '}
                    para precios y configuración.
                  </Text>
                </Box>

                <Box>
                  <Text weight={600} size="md" mb={8}>
                    ¿Ofrecen cuentas académicas?
                  </Text>
                  <Text size="sm" color="dimmed">
                    ¡Sí! Ofrecemos tarifas especiales para instituciones académicas.
                    Por favor, contacta con{' '}
                    <Text component="span" color="grape" sx={{ cursor: 'pointer' }}>
                      hi@streamdiscover.com
                    </Text>{' '}
                    para más información.
                  </Text>
                </Box>
              </Stack>
            </Stack>
          </Box>
          {/* Sección de contacto */}
          <Box mt={{ base: 40, sm: 50, md: 60 }}>
            <Text align="center" size="md" color="dimmed">
              ¿Necesitas ayuda para elegir?{' '}
              <Text
                component="span"
                color="grape"
                weight={500}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Contáctanos para una consulta personalizada
              </Text>
            </Text>
          </Box>
        </Stack>
      </Container>
    </>
  )
}

export default function Pricing({ topPlatforms }) {
  return (
    <Suspense
      fallback={
        <Container
          size="xl"
          py={{ base: 120, sm: 120, md: 120, lg: 100 }}
          px={{ base: 'md', sm: 'xl', md: 'xl', lg: 'xl' }}
          sx={(theme) => ({
            width: '85vw',
            maxWidth: '1366px',
            margin: '0 auto',
            [theme.fn.smallerThan('md')]: {
              paddingLeft: theme.spacing.md,
              paddingRight: theme.spacing.md,
              width: '100%'
            }
          })}
        >
          <Loader size="xl" variant="dots" />
        </Container>
      }
    >
      <PricingContent topPlatforms={topPlatforms} />
    </Suspense>
  )
}

Pricing.getLayout = function getLayout(page) {
  return (
    <Layout showSearchBar withAutocomplete showHero={false} pageName="Pricing">
      {page}
    </Layout>
  )
}

export async function getStaticProps({ locale }) {
  prerenderImportantLocales(locale)

  const messages = (await import(`../locales/${locale.split('-')[0]}/common.json`))
    .default

  const country = locale.split('-')[1].toLowerCase()

  const { data: topPlatformsResponse } = await searchAPI_v2(
    `/platforms/top?country=${country}`
  )

  return {
    props: {
      messages,
      topPlatforms: topPlatformsResponse.data
    }
  }
}
